/**
 * @file Account setup form.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';

import Alert from '../global/Alert';
import FormGroup from '../global/FormGroup';
import Select from '../global/Select';
import Button from '../global/Button';
import PhoneInput from '../global/PhoneInput';
import { cleanApolloError } from '../../util/normalize-errors';
import ACCOUNT_ROLES from '../../constants/account-roles';

class AccountSetupForm extends Component {
  static propTypes = {
    user: PropTypes.shape({
      id: PropTypes.string,
    }),
    // eslint-disable-next-line react/no-unused-prop-types
    error: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    loading: PropTypes.bool,
    updateAccount: PropTypes.func.isRequired,
    addProRoles: PropTypes.func.isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      error: '',
      contact_phone: '',
      account_pro_role: this.getDefaultProRole(),
      organization_name: '',
    };
  }

  componentDidUpdate({ loading: prevLoading, error: prevError }) {
    // Handling Apollo queries' prop injection
    const { error, loading } = this.props;

    if (error !== prevError) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(() => ({ error }));
    }
    if (loading !== prevLoading) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(() => ({ loading }));
    }
  }

  onInputChange = event => {
    event.preventDefault();

    const { name } = event.target;
    let { value } = event.target;

    // Strip spaces and force lowercase on usernames
    if (name === 'username') {
      value = value.replace(/ /g, '').toLowerCase();
    }

    this.setState(() => ({ [name]: value }));
  };

  onPhoneInputChange = phone => {
    this.setState(() => ({ contact_phone: phone }));
  };

  onFormSubmit = async event => {
    event.preventDefault();
    const { user, updateAccount, addProRoles, history } = this.props;
    const { account_pro_role: role } = this.state;
    this.setState(() => ({ error: '', loading: true }));

    try {
      // Add role to the account corresponding to their "Business Role"
      // selection in the form dropdown, and update their info
      const proRole = role === 'other' ? ACCOUNT_ROLES.PHOTOGRAPHER : role;

      await Promise.all([
        addProRoles(user.id, [proRole.toUpperCase()]),
        updateAccount({
          id: user.id,
          contact_phone: this.state.contact_phone,
          organization_name: this.state.organization_name,
          business_role: role.toUpperCase(),
        }),
      ]);

      history.replace('/app');
    } catch ({ message }) {
      this.setState(() => ({
        error: cleanApolloError(message),
        loading: false,
      }));
    }
  };

  getDefaultProRole = () => {
    const { location } = this.props;
    if (!location || !location.search) {
      return '';
    }

    const { search } = location;
    const { type } = qs.parse(search);
    switch (type) {
      case 'pro':
        return ACCOUNT_ROLES.PHOTOGRAPHER;
      case 'camps':
        return ACCOUNT_ROLES.CAMP_ADMIN;
      case 'schools':
        return ACCOUNT_ROLES.SCHOOL_ADMIN;
      case 'travel':
        return ACCOUNT_ROLES.RESORT_ADMIN;
      default:
        return '';
    }
  };

  render() {
    const { error } = this.state;

    return (
      <form className="form-account-setup" onSubmit={this.onFormSubmit}>
        {error && <Alert type="danger">{error}</Alert>}

        <FormGroup
          id="organization-name"
          type="text"
          label="Company Name"
          labelNote="(optional)"
          name="organization_name"
          value={this.state.organization_name}
          onChange={this.onInputChange}
        />

        <PhoneInput
          id="account-phone"
          label="Contact Phone"
          value={this.state.contact_phone || '+1'}
          onChange={this.onPhoneInputChange}
        />

        <Select
          id="account-role"
          label="I&rsquo;m a"
          name="account_pro_role"
          value={this.state.account_pro_role}
          onChange={this.onInputChange}
          required
        >
          <option value="">Choose from the list</option>
          <option value={ACCOUNT_ROLES.PHOTOGRAPHER}>
            Pro Photographer / Event Organizer
          </option>
          <option value={ACCOUNT_ROLES.CAMP_ADMIN}>Camp Administrator</option>
          <option value={ACCOUNT_ROLES.SCHOOL_ADMIN}>
            School Administrator
          </option>
          <option value={ACCOUNT_ROLES.RESORT_ADMIN}>
            Resort/Attraction Operator
          </option>
          <option value="other">Other</option>
        </Select>

        <Button type="submit" disabled={this.state.loading}>
          Complete Registration
        </Button>
      </form>
    );
  }
}

export default AccountSetupForm;
